/**
 * Storefront attribution capture.
 *
 * On every page load:
 *  - Read URL params, classify source platform, route utm_content to the
 *    correct ad-id column (meta_ad_id | google_ad_id | tiktok_ad_id).
 *  - Synchronize visitor/session identity when PostHog becomes ready.
 *  - First-touch: prefer values already in sessionStorage from a prior page
 *    in this session; only capture from URL on the first landing.
 *  - Stamp the collected attributes onto cart.attributes via
 *    POST /cart/update.js with verified responses and bounded retries.
 *  - Give ordinary checkout at most 1.2 seconds to finish attribution.
 *
 * Downstream consumers (do not break these contracts):
 *  - provelocitybat-lps webhook (/api/webhooks/shopify-order-paid) forwards
 *    note_attributes to PostHog as event properties.
 *  - provelocity-marketing daily Shopify cron reads note_attributes for
 *    per-ad ROAS rollups in Supabase (meta_ad_daily / google_ad_daily /
 *    tiktok_ad_daily — one table per platform, joined by the corresponding
 *    *_ad_id column).
 */
(function () {
	var STORAGE_KEY = 'pvb_attr';

	// Classify which platform stamped this URL so utm_content routes to the
	// right ad-id column. Defaults to Meta (dominant by historical spend
	// share — preserves backward-compat for any unflagged URLs).
	function classifyPlatform(params) {
		var source = (params.get('utm_source') || '').toLowerCase();
		if (source === 'google') return 'google';
		if (source === 'tiktok') return 'tiktok';
		if (source === 'meta' || source === 'facebook' || source === 'instagram') {
			return 'meta';
		}
		if (params.get('gclid')) return 'google';
		if (params.get('ttclid')) return 'tiktok';
		if (params.get('fbclid')) return 'meta';
		return 'unknown';
	}

	// Static URL-param → cart-attribute mapping. Note: utm_content is
	// handled separately below because its destination column depends on
	// the classified platform.
	var FIELD_MAP = {
		utm_term: 'meta_adset_id',
		utm_campaign: 'meta_campaign_id',
		pvb_creative: 'pvb_creative',
		pvb_placement: 'pvb_placement',
		fbclid: 'fbclid',
		utm_source: 'utm_source',
		utm_medium: 'utm_medium',
		ab_bucket: 'ab_bucket',
		ab_test: 'ab_test',
	};

	function readFromUrl() {
		var p = new URL(window.location.href).searchParams;
		var out = {};
		for (var k in FIELD_MAP) {
			var v = p.get(k);
			if (v) out[FIELD_MAP[k]] = v;
		}
		// utm_content routes by platform classifier into one of three columns.
		var utmContent = p.get('utm_content');
		if (utmContent) {
			var platform = classifyPlatform(p);
			var col;
			if (platform === 'google') col = 'google_ad_id';
			else if (platform === 'tiktok') col = 'tiktok_ad_id';
			else col = 'meta_ad_id';
			out[col] = utmContent;
		}
		return out;
	}

	function readFromStorage() {
		try {
			var raw = sessionStorage.getItem(STORAGE_KEY);
			return raw ? JSON.parse(raw) : {};
		} catch (e) {
			return {};
		}
	}

	function writeToStorage(a) {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(a));
		} catch (e) {
			/* private mode / quota exceeded — silently drop */
		}
	}

	function isEmpty(o) {
		for (var k in o) if (o[k]) return false;
		return true;
	}

	// First-touch: keep prior storage if non-empty; else capture from URL.
	function captureAttribution() {
		var stored = readFromStorage();
		if (!isEmpty(stored)) return stored;
		var fromUrl = readFromUrl();
		if (!isEmpty(fromUrl)) {
			writeToStorage(fromUrl);
			return fromUrl;
		}
		return {};
	}

	// Marketing stays first-touch; assignment and identity always use current state.
	var pending = Promise.resolve();
	var lastSaved = '';
	var replaying = false;
	var checkoutPending = false;
	var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

	function currentAttributes() {
		var attrs = Object.assign({}, captureAttribution());
		delete attrs.ab_test;
		delete attrs.ab_bucket;
		delete attrs.ph_distinct_id;
		delete attrs.session_id;
		var cookies = {};
		document.cookie.split(';').forEach(function (part) {
			var eq = part.indexOf('=');
			if (eq < 0) return;
			try { cookies[part.slice(0, eq).trim()] = decodeURIComponent(part.slice(eq + 1)); } catch (e) {}
		});
		var test = cookies['pvb-ab-test'];
		var bucket = cookies['pvb-ab-bucket'];
		if (test && (bucket === 'a' || bucket === 'b')) {
			attrs.ab_test = test;
			attrs.ab_bucket = bucket;
		}
		var ph = window.pvbPostHogReady;
		try {
			if (ph && !(ph.has_opted_out_capturing && ph.has_opted_out_capturing())) {
				var id = ph.get_distinct_id();
				var session = ph.get_session_id && ph.get_session_id();
				if (typeof id === 'string' && id) attrs.ph_distinct_id = id;
				if (typeof session === 'string' && session) attrs.session_id = session;
			}
		} catch (e) {}
		return attrs;
	}

	function writeCart(attrs) {
		var controller = new AbortController();
		var timer = setTimeout(function () { controller.abort(); }, 4000);
		return fetch(root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			credentials: 'same-origin',
			signal: controller.signal,
			body: JSON.stringify({ attributes: attrs })
		}).then(function (response) {
			if (!response.ok) throw new Error('Cart attribution HTTP ' + response.status);
			return response.json();
		}).then(function (cart) {
			var saved = cart.attributes || {};
			if (!Object.keys(attrs).every(function (key) { return String(saved[key]) === String(attrs[key]); })) {
				throw new Error('Cart attribution verification failed');
			}
		}).finally(function () { clearTimeout(timer); });
	}

	function syncCart(force) {
		pending = pending.catch(function () {}).then(function () {
			var attrs = currentAttributes();
			var signature = JSON.stringify(attrs);
			if (isEmpty(attrs) || (!force && signature === lastSaved)) return;
			return writeCart(attrs).catch(function () {
				// Re-read identity/assignment if PostHog became ready during the first attempt.
				attrs = currentAttributes();
				return writeCart(attrs);
			}).then(function () {
				lastSaved = JSON.stringify(attrs);
				if (new URLSearchParams(window.location.search).has('pvb_qa')) {
					console.info('[PVB attribution] Verified cart attributes:', Object.keys(attrs).join(', '));
				}
			}).catch(function (error) {
				lastSaved = '';
				console.warn('[PVB attribution] Cart save failed; checkout remains available.', error && error.message);
			});
		});
		return pending;
	}

	function beforeCheckout(resume) {
		if (checkoutPending) return;
		checkoutPending = true;
		var finished = false;
		var timer;
		function finish() {
			if (finished) return;
			finished = true;
			checkoutPending = false;
			clearTimeout(timer);
			replaying = true;
			try { resume(); } finally { replaying = false; }
		}
		timer = setTimeout(finish, 1200);
		syncCart(true).then(finish, finish);
	}

	// Only ordinary checkout controls are intercepted. Accelerated checkout is separate.
	document.addEventListener('click', function (event) {
		if (replaying || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		var target = event.target.closest && event.target.closest('a[href], button[name="checkout"], input[name="checkout"]');
		if (!target || target.disabled) return;
		if (target.tagName === 'A') {
			var url = new URL(target.href, window.location.href);
			if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname) || (target.target && target.target !== '_self')) return;
		}
		event.preventDefault();
		event.stopImmediatePropagation();
		beforeCheckout(function () { target.click(); });
	}, true);

	document.addEventListener('submit', function (event) {
		if (replaying || event.defaultPrevented) return;
		var form = event.target;
		var button = event.submitter;
		var url = new URL(form.action, window.location.href);
		if (url.origin !== window.location.origin) return;
		if (!/\/checkout\/?$/.test(url.pathname) && !(button && button.name === 'checkout')) return;
		if (!form.requestSubmit) return;
		event.preventDefault();
		event.stopImmediatePropagation();
		beforeCheckout(function () { form.requestSubmit(button || undefined); });
	}, true);

	document.addEventListener('pvb:posthog-ready', function () { syncCart(false); });
	window.addEventListener('pageshow', function (event) { if (event.persisted) syncCart(true); });
	window.addEventListener('online', function () { syncCart(true); });
	syncCart(false);
})();
